import { useState } from 'react';
import { Pen, UsersRound, Users, Sparkles } from 'lucide-react';
import Notepad from './Notepad';
import Groups from './Groups';
import Classroom from './Classroom';
import PageNarration from './PageNarration';

const MODES = [
  { id: 'notepad', label: 'NOTEPAD', icon: Pen, subtitle: 'Solo research' },
  { id: 'groups', label: 'GROUPS', icon: UsersRound, subtitle: 'Peer collaboration' },
  { id: 'classroom', label: 'CLASSROOM', icon: Users, subtitle: 'Teacher-led' },
];

export default function WorkCenter() {
  const [mode, setMode] = useState('notepad');

  return (
    <div className="px-5 md:px-10 lg:px-16 py-8 md:py-12 max-w-6xl mx-auto w-full space-y-6" data-testid="work-center">
      {/* Apr 30 2026 iter_67 per Lead: "Work Center enlarged all text.
          At top coming soon with help of verified founding teachers."
          New coming-soon banner + full-width narration button + bumped
          headline + subtitle font sizes. */}
      <div
        className="rounded-md border border-[#D4A84A]/55 bg-[#0A0A0A] px-6 md:px-8 py-5"
        style={{ boxShadow: '0 0 28px rgba(212,168,74,0.12)' }}
        data-testid="work-center-coming-soon"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#D4A84A] font-hud text-sm tracking-[0.3em] uppercase mb-2">
              <Sparkles size={16} /> Coming soon
            </div>
            <h1 className="font-historical text-white font-light text-3xl md:text-4xl lg:text-5xl leading-tight">
              Coming soon — with the help of our verified founding teachers.
            </h1>
            <p className="font-historical text-white/80 text-lg md:text-xl lg:text-2xl mt-3 leading-relaxed">
              We are building lesson plans, classroom discussion prompts, and export tools together.
              If you want to help shape how kids walk history in real classrooms, reach out through the
              Contact page and we will enroll you in the founding teachers cohort.
            </p>
          </div>
          <PageNarration pageKey="workcenter" label="Listen" />
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
            Work Center
          </h2>
          <p className="font-hud text-sm md:text-base text-white/80 tracking-wider mt-2">
            NOTEPAD · GROUPS · CLASSROOM — YOU CAN BELONG TO ALL THREE
          </p>
        </div>

        <div
          className="inline-flex items-center border border-[#2F8A92]/15 rounded overflow-hidden"
          data-testid="work-mode-toggle"
        >
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                data-testid={`mode-${m.id}`}
                onClick={() => setMode(m.id)}
                className={`flex items-center gap-2 px-4 md:px-5 py-2.5 font-hud text-xs md:text-sm tracking-widest transition-all duration-300 ${
                  active
                    ? 'bg-[#2F8A92]/20 text-[#2F8A92] border-r border-[#2F8A92]/15 last:border-0'
                    : 'text-white/80 hover:text-[#2F8A92] border-r border-[#2F8A92]/10 last:border-0'
                }`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <p className="font-mono text-sm md:text-base text-white/70" data-testid="mode-hint">
        {mode === 'notepad' && 'Solo workspace. Auto-saves every 10 seconds. Methodology prompts appear when you tag a section with a chain step.'}
        {mode === 'groups' && 'Peer research groups (up to 12). Any paid user can create a group. Export notes here from the Notepad tab.'}
        {mode === 'classroom' && 'Teacher-led classrooms. Teachers assign chains, students submit, teachers rate using the Chain Keeper Rating.'}
      </p>

      <div className="pt-2">
        {mode === 'notepad' && <Notepad />}
        {mode === 'groups' && <Groups />}
        {mode === 'classroom' && <Classroom />}
      </div>
    </div>
  );
}
